import { Key, memo, useState } from "react";
import { ColumnsType } from "antd/lib/table";
import { html } from "../../../utils/html";
import { DocEditor } from "../document-decentralize-title-table";
import { arrayNest } from "../document-view-decentralize";
import { Table } from "../../../../../components";
import { TableRowSelection } from "antd/lib/table/interface";
import { DownCircleTwoTone } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { getEnv } from "../../../../../AppConfigs";
import chunk from "lodash/chunk";
import api from "../../../../../services/axiosBase";
import { BATCHING_URL } from "../../../../../constants";
import { message } from "antd";
import EditorGrantedUsers from "./editor-granted-users";
import { usePublishDocumentContent } from "../../../hooks/document-content/use-publish-document-content";
import DecentralizeContentTitleTable from "./decentralize-content-title-table";

const SPSiteId = getEnv("REACT_APP_SP_SITE_ID");
const SPListId = getEnv("REACT_APP_DOCUMENT_CONTENTS_LIST_ID");

type DataType = IDocumentContent & { children?: IDocumentContent[] };

export interface IDocumentContent {
  Id?: string;
  Content: string;
  CustomId: string;
  CustomParentId?: string;
  Permissions?: DocEditor[];
  Title: string;
  children?: IDocumentContent[];
  IsContent: boolean;
  CreatedUser: { email: string; id: string };
  docEditors: any;
}

const generateRequestBatching = (
  data: {
    rowKey: Key;
    Permissions?: DocEditor[];
  }[],
) => {
  return data.map((item, idx) => ({
    id: (idx + 1).toString(),
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    url: `/sites/${SPSiteId}/lists/${SPListId}/items/${item.rowKey}/fields`,
    body: { Permissions: JSON.stringify(item.Permissions) },
  }));
};

const updateDocumentUserPermission = async (body: { requests: any[] }) => {
  const chunked = chunk(body.requests, 15);
  return Promise.all(chunked.map(async (requests) => await api.post(BATCHING_URL, { requests })));
};

const EditorViewDecentralize = () => {
  const { isLoading: isDocumentContentLoading, data: contents = [] } = usePublishDocumentContent();
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const loading = isDocumentContentLoading || isLoading;
  const disabledSelectUsers = selectedRowKeys.length === 0;
  const queryClient = useQueryClient();

  const toggleLoading = (loading: boolean) => {
    setIsLoading(loading);
  };

  const handleUpdateDocument = async ({ requests, reset }: { requests: any[]; reset: () => void }) => {
    try {
      toggleLoading(true);
      await updateDocumentUserPermission({ requests });
      message.success("Cấp quyền thành công");
      reset();
      queryClient.invalidateQueries({ queryKey: ["publish-document-content"] });
    } catch (error) {
      message.error("Cấp quyền thất bại");
    } finally {
      toggleLoading(false);
    }
  };

  const onDecentralizeRead = async (_: string[], docEditors: DocEditor[], { reset }: { reset: () => void }) => {
    const result = selectedRowKeys.map((rowKey: any) => {
      return { rowKey, Permissions: [...docEditors] };
    });

    const requests = generateRequestBatching(result);

    handleUpdateDocument({ requests: requests, reset });
  };

  const onRevoke = async ({ id, user }: { id: string; user: DocEditor }, content: IDocumentContent) => {
    const userPermissions: any[] = content?.Permissions ?? [];

    const filteredUserPermissions = userPermissions.filter((item) => item?.id !== id);
    const requests = generateRequestBatching([{ rowKey: content.Id as Key, Permissions: filteredUserPermissions }]);
    try {
      toggleLoading(true);
      await updateDocumentUserPermission({ requests });
      message.success("Xóa quyền thành công");
      queryClient.invalidateQueries({ queryKey: ["publish-document-content"] });
    } catch (error) {
      console.error(error);
      message.error("Xóa quyền thất bại");
    } finally {
      toggleLoading(false);
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Nội dung",
      dataIndex: "Content",
      key: "Content",
      width: "720px",
      render: (value: string) => {
        const doc = html.htmlStringToObject(value);
        const tree = html.getTreeData(doc);
        const htmlString = html.objectToHtmlString(tree);
        return <div style={{ width: "720px" }} dangerouslySetInnerHTML={{ __html: htmlString }} />;
      },
    },
    {
      title: (
        <DecentralizeContentTitleTable
          disabled={disabledSelectUsers}
          label="Danh sách người xem"
          onSubmit={onDecentralizeRead}
        />
      ),
      dataIndex: "Permissions",
      key: "readUsers",
      width: "400px",
      render: (users, content) => (
        <EditorGrantedUsers Permissions={users} onRevoke={(data) => onRevoke(data, content)} />
      ),
    },
  ];

  const rowSelection: TableRowSelection<IDocumentContent> = {
    checkStrictly: false,
    selectedRowKeys,
    onChange: (value) => setSelectedRowKeys(value),
  };

  const mappedDocuments = arrayNest<IDocumentContent>(contents, {
    idFieldName: "CustomId",
    parentIdFieldName: "CustomParentId",
  });

  return (
    <Table
      rowKey="Id"
      loading={loading}
      bordered
      className="ant-table-row-actions-disabled"
      columns={columns}
      rowSelection={rowSelection}
      dataSource={mappedDocuments}
      pagination={false}
      scroll={{ x: 1000, y: 600 }}
      expandable={{
        defaultExpandAllRows: true,
        expandIcon: ({ expanded, onExpand, record }) => {
          if (record.children?.length === 0) return null;
          return (
            <DownCircleTwoTone
              rotate={expanded ? 0 : 180}
              style={{ fontSize: 20 }}
              rev="data"
              onClick={(e) => onExpand(record, e)}
            />
          );
        },
      }}
    />
  );
};

export default memo(EditorViewDecentralize);
